import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Pause,
  Play,
  Repeat2,
  Settings,
} from 'lucide-react'
import { LegacyAudioSettings, useLegacyAudioController } from '../legacy-audio-settings'
import { MediaControlBar } from '../../media-control-bar'
import { LmsButton } from '@shared/ui'

export interface LegacyAudioRailProps {
  readonly autoPlayKey?: string
  readonly autoPlayOnLoad?: boolean
  readonly className?: string
  readonly showAdvancedControls?: boolean
  readonly source?: string
  readonly variant?: 'audioBook' | 'flashcard' | 'listeningQuiz' | 'selfStudyBoxAudio'
  readonly withImage?: boolean
}

export function LegacyAudioRail({
  autoPlayKey,
  autoPlayOnLoad = false,
  className = '',
  showAdvancedControls,
  source,
  variant = 'selfStudyBoxAudio',
  withImage = false,
}: LegacyAudioRailProps) {
  const { t } = useTranslation('translation')
  const isFlashcard = variant === 'flashcard'
  const {
    audioElementProps,
    handleCloseSettings,
    handleLoopLevelChange,
    handlePlaybackRateChange,
    handleSeekPercent,
    handleToggleLoop,
    handleTogglePlayback,
    isLoopActive,
    isPlaying,
    loopDashOffset,
    loopLevel,
    loopRemaining,
    playbackRate,
    progress,
    settingsAnchor,
    settingsSection,
    setSettingsAnchor,
    setSettingsSection,
  } = useLegacyAudioController({
    autoPlayKey,
    autoPlayOnLoad,
    source,
    variant,
  })

  const hasAdvancedControls = showAdvancedControls ?? autoPlayOnLoad

  if (!source) {
    return null
  }

  return (
    <MediaControlBar
      aria-label={isFlashcard ? t('learningUi.Audio controls', { defaultValue: 'Audio controls' }) : 'Audio controls'}
      className={`legacySelfStudyAudioRail${withImage ? ' withImage' : ''} ${className}`.trim()}
      data-audio-controller={variant}
      kind="audio"
      style={
        {
          '--legacy-self-study-audio-progress': `${progress}%`,
          '--lms-media-control-progress': `${progress}%`,
        } as CSSProperties
      }
    >
      <LmsButton
        aria-label={isFlashcard
          ? t(isPlaying ? 'learningUi.Pause audio' : 'learningUi.Play audio', { defaultValue: isPlaying ? 'Pause audio' : 'Play audio' })
          : isPlaying ? 'Pause audio' : 'Play audio'}
        className="lmsMediaControlButton"
        onClick={handleTogglePlayback}
        type="button"
        variant="ghost"
      >
        {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
      </LmsButton>
      <input
        aria-label={isFlashcard ? t('learningUi.Audio progress', { defaultValue: 'Audio progress' }) : 'Audio progress'}
        className="lmsMediaControlProgress legacySelfStudyAudioProgress"
        max={100}
        min={0}
        onChange={(event) => {
          handleSeekPercent(Number(event.currentTarget.value))
        }}
        step={1}
        type="range"
        value={progress}
      />
      {hasAdvancedControls ? (
        <>
          <LmsButton
            aria-label={isFlashcard
              ? t(isLoopActive ? 'learningUi.Stop looping audio ({{count}} remaining)' : 'learningUi.Loop audio {{count}} times', {
                count: isLoopActive ? loopRemaining : loopLevel,
                defaultValue: isLoopActive ? 'Stop looping audio ({{count}} remaining)' : 'Loop audio {{count}} times',
              })
              : isLoopActive ? `Stop looping audio (${loopRemaining} remaining)` : `Loop audio ${loopLevel} times`}
            className={`lmsMediaControlButton legacySelfStudyAudioControl${isLoopActive ? ' active' : ''}`}
            onClick={handleToggleLoop}
            type="button"
            variant="ghost"
          >
            {isLoopActive ? (
              <>
                <span
                  className="legacySelfStudyAudioLoopProgress"
                  style={
                    {
                      '--legacy-self-study-loop-offset': loopDashOffset,
                    } as CSSProperties
                  }
                >
                  <svg aria-hidden="true" viewBox="0 0 120 120">
                    <circle
                      className="legacySelfStudyAudioLoopProgressBase"
                      cx="60"
                      cy="60"
                      r="50"
                    />
                    <circle
                      className="legacySelfStudyAudioLoopProgressActive"
                      cx="60"
                      cy="60"
                      r="50"
                    />
                  </svg>
                </span>
                <span className="legacySelfStudyAudioLoopCount">{loopRemaining}</span>
              </>
            ) : (
              <Repeat2 aria-hidden="true" />
            )}
          </LmsButton>
          <LmsButton
            aria-controls={settingsAnchor ? 'legacy-self-study-audio-settings' : undefined}
            aria-expanded={Boolean(settingsAnchor)}
            aria-haspopup="dialog"
            aria-label={isFlashcard ? t('learningUi.Audio settings', { defaultValue: 'Audio settings' }) : 'Audio settings'}
            className={`lmsMediaControlButton legacySelfStudyAudioControl${settingsAnchor ? ' active' : ''}`}
            onClick={(event) => {
              setSettingsAnchor(event.currentTarget)
            }}
            type="button"
            variant="ghost"
          >
            <Settings aria-hidden="true" />
          </LmsButton>
        </>
      ) : null}
      <CachedLearningAudio {...audioElementProps} />
      {hasAdvancedControls ? (
        <LegacyAudioSettings
          anchorEl={settingsAnchor}
          id="legacy-self-study-audio-settings"
          loopLevel={loopLevel}
          onChangeLoop={handleLoopLevelChange}
          onChangeSection={setSettingsSection}
          onChangeSpeed={handlePlaybackRateChange}
          onClose={handleCloseSettings}
          panelClassName="legacySelfStudyAudioSettingsPanel"
          playbackRate={playbackRate}
          section={settingsSection}
        />
      ) : null}
    </MediaControlBar>
  )
}
